import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './UserInfo.sass'
import ProfileViewModel from '../../ProfileViewModel'
import {
  Form,
  FormRow,
  TextInputContainer,
  FormColumns,
  FormColumn,
  FormActions,
  FormGroup,
} from 'components/inputs'
import Help from 'components/inputs/Help'
import Button from 'components/Button'
import ProfileGeneralViewModel, {
  MemberMetadataInput,
} from '../ProfileGeneralViewModel'
import { isNotNil } from 'utils/object-util'
import TextInput from 'components/inputs/TextInput'
import NumericInput from 'components/inputs/NumericInput'
import Checkbox from 'components/Checkbox'

interface UserInfoProps {
  vm: ProfileViewModel
}

export default observer(function UserInfo({ vm }: UserInfoProps) {
  return (
    <div className={cx(styles.root)}>
      <Form onSubmit={vm.general.saveProfile}>
        <FormRow>
          <FormColumns>
            <FormColumn>
              <TextInputContainer
                model={vm.general}
                error={vm.general.validation.getError('givenName')}
                field="givenName"
                label="First Name"
                placeholder="First Name"
                disabled={!vm.general.canEdit}
              />
            </FormColumn>
            <FormColumn>
              <TextInputContainer
                model={vm.general}
                error={vm.general.validation.getError('familyName')}
                field="familyName"
                label="Last Name"
                placeholder="Last Name"
                disabled={!vm.general.canEdit}
              />
            </FormColumn>
          </FormColumns>
        </FormRow>
        <FormRow>
          <FormColumns>
            <FormColumn>
              <TextInputContainer
                model={vm.general}
                error={vm.general.validation.getError('email')}
                field="email"
                type="email"
                label="Email Address"
                placeholder="Email Address"
                disabled={!vm.general.canEdit}
              />
            </FormColumn>
            <FormColumn>
              <TextInputContainer
                model={vm.general}
                error={vm.general.validation.getError('phone')}
                type="tel"
                field="phone"
                label="Phone"
                placeholder="Phone number"
                disabled={!vm.general.canEdit}
              />
            </FormColumn>
          </FormColumns>
        </FormRow>
        {vm.general.inputFields.map((field, index) => (
          <FormRow key={index}>
            <FormGroup label={field.title}>
              <InputField field={field} vm={vm.general} />
            </FormGroup>
          </FormRow>
        ))}
        <FormActions>
          <Button
            submit
            type="primary"
            loading={vm.general.saveProfile.pending}
            disabled={!vm.general.canSave}
            title={
              vm.general.canSave
                ? 'Save changes to your basic profile'
                : vm.general.saveProfile.pending
                ? 'Saving...'
                : 'No changes to save'
            }
          >
            Save
          </Button>
        </FormActions>
      </Form>
    </div>
  )
})

interface CustomInputFieldProps {
  field: MemberMetadataInput
  vm: ProfileGeneralViewModel
}

const InputField = observer(function InputField({
  field,
  vm,
}: CustomInputFieldProps) {
  const value =
    isNotNil(vm.memberMetadata[field.id]) && vm.memberMetadata[field.id]
  switch (field.type) {
    case 'NUMERIC':
      return (
        <>
          <NumericInput
            disabled={!field.permissions.includes('USER_WRITE')}
            precision={2}
            min={0}
            step={1}
            value={value as number}
            onChange={(v) => vm.onMetadataChange(field.id, v)}
          />
          <Help help={field.description} className={styles.spacing} />
        </>
      )
    case 'TEXT':
      return (
        <>
          <TextInput
            disabled={!field.permissions.includes('USER_WRITE')}
            type="text"
            value={(value as string) || ''}
            onChange={(e) => vm.onMetadataChange(field.id, e.target.value)}
          />
          <Help help={field.description} className={styles.spacing} />
        </>
      )
    case 'BOOL':
      return (
        <div className={styles.checkboxContainer}>
          <Checkbox
            disabled={!field.permissions.includes('USER_WRITE')}
            checked={value as boolean}
            onChange={() => vm.onMetadataChange(field.id, !value)}
          />
          <Help help={field.description} />
        </div>
      )
    default:
      return <></>
  }
})
